import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { CaretDownIcon, CaretUpIcon } from '@radix-ui/react-icons';
import { IconButton, TextField } from '@radix-ui/themes';
import './PropertyInput.css';
function clampValue(parsed, min, max) {
    let result = parsed;
    if (min !== undefined) {
        result = Math.max(result, min);
    }
    if (max !== undefined) {
        result = Math.min(result, max);
    }
    return result;
}
function parseCommittedValue(text, step) {
    const parsed = Number.parseFloat(text);
    if (Number.isNaN(parsed)) {
        return null;
    }
    if (Number.isInteger(step)) {
        return Math.round(parsed);
    }
    const decimals = String(step).includes('.') ? String(step).split('.')[1].length : 0;
    return Number(parsed.toFixed(decimals));
}
export function PropertyInput({ label, value, onChange, min, max, step = 1, disabled = false, className, 'aria-label': ariaLabel, }) {
    const [text, setText] = useState(String(value));
    const isFocusedRef = useRef(false);
    const accessibleName = ariaLabel ?? label ?? 'Value';
    useEffect(() => {
        if (!isFocusedRef.current) {
            setText(String(value));
        }
    }, [value]);
    const applyValue = (next) => {
        const clampedValue = clampValue(next, min, max);
        setText(String(clampedValue));
        if (clampedValue !== value) {
            onChange(clampedValue);
        }
    };
    const commit = () => {
        const parsedValue = parseCommittedValue(text, step);
        if (parsedValue === null) {
            setText(String(value));
            return;
        }
        applyValue(parsedValue);
    };
    const nudge = (direction) => {
        if (disabled)
            return;
        applyValue(value + direction * step);
    };
    const canDecrement = min === undefined || value > min;
    const canIncrement = max === undefined || value < max;
    return (_jsxs("div", { className: `property-input-group${className ? ` ${className}` : ''}`, children: [label ? _jsx("label", { className: "property-label", children: label }) : null, _jsx(TextField.Root, { size: "1", variant: "surface", radius: "medium", className: "property-number-field", type: "text", inputMode: "numeric", value: text, disabled: disabled, "aria-label": accessibleName, onChange: (e) => setText(e.target.value), onFocus: () => {
                    isFocusedRef.current = true;
                }, onBlur: () => {
                    isFocusedRef.current = false;
                    commit();
                }, onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                        e.currentTarget.blur();
                        return;
                    }
                    if (e.key === 'Escape') {
                        setText(String(value));
                        e.currentTarget.blur();
                        return;
                    }
                    if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        nudge(1);
                        return;
                    }
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        nudge(-1);
                    }
                }, children: _jsx(TextField.Slot, { side: "right", px: "1", gap: "0", children: _jsxs("div", { className: "property-number-steppers", children: [_jsx(IconButton, { type: "button", size: "1", variant: "ghost", color: "gray", highContrast: true, className: "property-number-step", "aria-label": `Increase ${accessibleName}`, disabled: disabled || !canIncrement, tabIndex: -1, onMouseDown: (e) => e.preventDefault(), onClick: () => nudge(1), children: _jsx(CaretUpIcon, {}) }), _jsx(IconButton, { type: "button", size: "1", variant: "ghost", color: "gray", highContrast: true, className: "property-number-step", "aria-label": `Decrease ${accessibleName}`, disabled: disabled || !canDecrement, tabIndex: -1, onMouseDown: (e) => e.preventDefault(), onClick: () => nudge(-1), children: _jsx(CaretDownIcon, {}) })] }) }) })] }));
}
